import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '@templates/index';
import { Button, Card, Input } from '@atoms/index';
import { authService } from '@services/authService';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    if (!email.trim() || !email.includes('@')) {
      setError('Lütfen geçerli bir e-posta adresi girin');
      setIsLoading(false);
      return;
    }

    try {
      await authService.forgotPassword(email.trim());
      setSuccess(true);
    } catch (err: any) {
      setError(
        err.response?.data?.error?.message || 'Şifre sıfırlama e-postası gönderilemedi. Lütfen tekrar deneyin.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8 animate-fade-in">
            <div className="inline-block p-4 rounded-3xl bg-gradient-to-br from-rose-500 to-pink-600 shadow-premium mb-6">
              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                />
              </svg>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-3">
              Şifremi Unuttum
            </h1>
            <p className="text-gray-600 text-lg">
              E-posta adresinize şifre sıfırlama bağlantısı göndereceğiz
            </p>
          </div>

          <Card padding="none" className="overflow-hidden backdrop-blur-xl animate-scale-in">
            <div className="p-8">
              {success ? (
                <div className="space-y-6">
                  <div className="p-4 rounded-xl bg-success/10 border border-success/30">
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-success flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm text-success font-medium">
                        Şifre sıfırlama bağlantısı e-posta adresinize gönderildi
                      </p>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 text-center">
                    E-posta adresinizi kontrol edin ve şifre sıfırlama bağlantısına tıklayın.
                    Bağlantı 1 saat geçerlidir.
                  </p>

                  <Button
                    type="button"
                    variant="primary"
                    size="lg"
                    fullWidth
                    onClick={() => navigate('/login')}
                  >
                    Giriş Sayfasına Dön
                  </Button>
                </div>
              ) : (
                <>
                  {error && (
                    <div className="mb-6 p-4 rounded-xl bg-error/10 border border-error/30">
                      <div className="flex items-center gap-3">
                        <svg className="w-5 h-5 text-error flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <p className="text-sm text-error font-medium">{error}</p>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        E-posta
                      </label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="ornek@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={isLoading}
                        required
                        className="w-full"
                      />
                    </div>

                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      fullWidth
                      disabled={isLoading || !email.trim()}
                      isLoading={isLoading}
                    >
                      Şifre Sıfırlama Bağlantısı Gönder
                    </Button>
                  </form>

                  <div className="mt-6 text-center">
                    <button
                      onClick={() => navigate('/login')}
                      className="text-sm text-gray-600 hover:text-primary font-medium transition-colors"
                    >
                      ← Giriş sayfasına dön
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="px-8 py-6 bg-gray-50/50 border-t border-gray-100">
              <p className="text-xs text-gray-500 text-center leading-relaxed">
                Eğer bu e-posta adresi kayıtlıysa, şifre sıfırlama bağlantısı gönderilecektir.
                Güvenlik nedeniyle, e-posta adresinin kayıtlı olup olmadığını belirtmeyiz.
              </p>
            </div>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default ForgotPasswordPage;
